import { IAccount } from "../Accounting/store"
import { IAddress } from "./../Addresses"
import { IContact } from "./../Contacts"
import { IHotelGroup } from "./../HotelGroups"
import { IMealPlan } from "./../MealPlans"
import { IRoomType } from "./../RoomTypes"
import { THotelNote } from "../HotelNotes/store"
import { TMedia } from "./../Media"
import { TTripDestination } from "../TripDestinations"
import { IUser } from "../Auth"
import useSWR from "swr"
import {
	ISimpleListResponse,
	IListResponse,
	useXHR,
	XHRInstance,
} from "@sembark-travel/xhr"
import { parseDate } from "@sembark-travel/datetime-utils"
import type { TCurrencyCode } from "@sembark-travel/money"
import { ISupplier } from "../Supplier"
import { IPaymentPreference } from "../SupplierPaymentPreferences"
import { isTruthy } from "@sembark-travel/ui-form"
import type { TPolicyHeadSummary } from "../shared/policies"
import type { TAmenity } from "../Amenities"
import type { TChannelManager } from "../ChannelManagers"
import type { THotelOtaMealPlan } from "../HotelOtaMealPlans"
import type { THotelOtaRoomType } from "../HotelOtaRoomTypes"
import { TOtaRatePlan } from "../HotelOtaRatePlans"

export type TDateInterval = {
	start_date: Date
	end_date: Date
}

export type TGroupedRoomInterval = {
	black_out_date_intervals: TDateInterval[]
	room_types: {
		name: string
		value: number
	}[]
	blackout: boolean | 1 | 0
}

export type TSoldoutRoomInterval = {
	black_out_date_intervals: TDateInterval[]
	room_types: {
		name: string
		value: number
	}[]
	blackout: false | 0
}

export type TBlackoutRoomInterval = {
	black_out_date_intervals: TDateInterval[]
	room_types: {
		name: string
		value: number
	}[]
	blackout: true | 1
}

export interface IPrice {
	id: number
	hotel_id: number
	base_price: number
	persons: number
	adult_with_extra_bed_price: number
	child_with_extra_bed_price: number
	child_without_extra_bed_price: number
	start_date: string
	end_date: string
	meal_plan_id: number
	room_type_id: number
	meal_plan: IMealPlan
	room_type: IRoomType
}

export interface IHotelRoomType extends IRoomType {
	allowed_extra_beds: number
	allowed_adults_with_extra_bed: number
	allowed_children_with_extra_bed: number
	allowed_children_without_extra_bed?: number
	no_of_rooms?: number
	black_out_date_intervals: Array<{
		start_date: string
		end_date: string
		id: string
		name: string
		stop_sales: boolean | 1 | 0
	}>
	// Present only when this row is a clone of an OTA/CM room type (BE
	// maintains the clone + mapping for versioning — a name/config change
	// upstream creates a new local row rather than mutating this one, so a
	// past quote's snapshot stays stable). Occupancy constraints for a
	// remote selection are read from here, not duplicated onto this row's
	// own top-level fields above.
	ota_room_type?: THotelOtaRoomType //Available only inside the ari api response (will be missing in the quote hotel /edit quote api).
}
export interface IHotelMealPlan extends IMealPlan {
	ota_meal_plan?: THotelOtaMealPlan //Available only inside the ari api response (will be missing in the quote hotel /edit quote api).
}

export type IHotelAddress = IAddress

export type THotelLocation = {
	id: number
	name: string
	short_name: string
}

export type THotelOtaChildAge = {
	id: number
	// format "CNB (6-12)" or "CWEB (12-18) and not null"
	occupancy: string
	pax_type: THotelOtaChildAgePaxType
	start_age: number
	end_age: number
	// resolved value: the mapping, or the channel manager's default when unmapped.
	// null for a hotel on no channel manager
	remote_pax_type: string | null
}

export type THotelRemotePaxTypes = {
	// null for a hotel on no channel manager
	channel_manager: TChannelManager | null
	remote_pax_types: Array<string>
	default_adult_remote_pax_type: string | null
	default_child_remote_pax_type: string | null
}

export type THotelPaxTypeCode = "BASE" | "AWEB" | "CWEB" | "CNB" | "A" | "COMP"

export type THotelOtaChildAgePaxType = Extract<
	THotelPaxTypeCode,
	"CNB" | "CWEB" | "COMP"
>

// `remote_pax_type`s are omitted when the hotel isn't on a channel manager.
// null leaves the pax type unmapped, so the channel manager's default applies
export type TOtaChildAgesPayload = {
	min_age: number
	max_age: number
	aweb_remote_pax_type?: string | null
	ages: Array<{
		pax_type: THotelOtaChildAgePaxType
		start_age: number
		end_age: number
		remote_pax_type?: string | null
	}>
}

/**
 * Channel manager connected with a hotel. `null` when the hotel's inventory is
 * not managed by any channel manager.
 */
export type THotelChannelManager = TChannelManager

export type THotelOtaRatePlan = TOtaRatePlan

export type THotelCalendarAriRateSheet = {
	id: number
	rate_sheet_head_id: number
	name: string
	version: number
}

// What every day carries, whichever way its rate is set
type THotelCalendarAriDayBase = {
	date: string
	// null when nothing is known for the day; 0 is sold out
	available: number | null
	is_sold_out: boolean
	is_stop_sell: boolean
	bookings_count: number
}

// A priced day always has a currency and a base rate, one rate per occupancy
type THotelCalendarAriDayRates = {
	currency: TCurrencyCode
	base_rate_before_tax: string
	rates: { occupancy: string; rate_before_tax: string }[]
}

// `rate_source` tells which way the day's rate is set. A day with no rate
// source has no rate at all.
export type THotelCalendarAriDay = THotelCalendarAriDayBase &
	(
		| (THotelCalendarAriDayRates & {
				rate_source: "rate_sheet"
				rate_sheet: THotelCalendarAriRateSheet
				rate_sheet_block_id: number
				rate_plan: null
		  })
		| (THotelCalendarAriDayRates & {
				rate_source: "calendar"
				rate_sheet: null
				rate_sheet_block_id: null
				rate_plan: Pick<THotelOtaRatePlan, "id" | "name"> & {
					channel_manager: THotelChannelManager
				}
		  })
		| {
				rate_source: null
				rate_sheet: null
				rate_sheet_block_id: null
				rate_plan: null
				currency: null
				base_rate_before_tax: null
				rates: []
		  }
	)

export type THotelCalendarAriRoomType = {
	ota_room_type: Pick<
		THotelOtaRoomType,
		"id" | "name" | "no_of_rooms" | "base_adult_occupancy" | "max_occupancy"
	>
	// lines up with the top level `dates`
	dates: Array<THotelCalendarAriDay>
}

export type THotelCalendarAri = {
	hotel_id: number
	ota_meal_plan_id: number
	trip_source_id: number | null
	dates: Array<string>
	ota_room_types: Array<THotelCalendarAriRoomType>
}

export interface IHotel {
	id: number
	// the short code the hotel is referred to by from outside, fixed for its
	// lifetime. Only the full hotel resource carry it
	uid: string
	name: string
	stars?: string
	currency: string
	no_of_stars?: number | null
	stars_string?: string | null
	extra_bed_child_age_start: number
	extra_bed_child_age_end: number
	meal_plans: IHotelMealPlan[]
	ota_meal_plans: THotelOtaMealPlan[]
	entry_type: "manually" | "by_sheet" | "by_preload"
	room_types: IHotelRoomType[]
	ota_room_types: THotelOtaRoomType[]
	address: IHotelAddress
	prices?: IPrice[]
	contact?: IContact
	contacts?: IContact[]
	notes?: THotelNote[]
	global_notes?: THotelNote[] // append by frontend on selectHoles
	payment_preference?: IPaymentPreference
	cancellation_policy_head?: TPolicyHeadSummary | null
	payment_policy_head?: TPolicyHeadSummary | null
	cancellation_policy_head_id?: number | null
	payment_policy_head_id?: number | null
	checkin_at_local?: string
	checkin_at: string
	checkout_at_local?: string
	checkout_at: string
	child_age_breakdowns?: Array<{
		start_age: number
		end_age: number
		cnb_allowed: boolean
		cweb_allowed: boolean
	}>
	ota_child_ages: THotelOtaChildAge[]
	// resolved value of the extra bed's mapping, null for a hotel on no channel manager
	aweb_remote_pax_type: string | null
	pax_type_of_childs: {
		occupancy: string
		pax_type: THotelPaxTypeCode
		pax_count: number
	}[]
	accounting_accounts?: Array<IAccount>
	group?: IHotelGroup
	latest_media?: TMedia | null
	thumb_image_url?: string | null
	trip_destinations?: Array<TTripDestination>
	created_at: string
	created_by?: IUser
	updated_at: string | null
	updated_by?: IUser
	deleted_at: string | null
	deleted_by?: IUser
	url?: string
	location: THotelLocation
	rate_expiry_date: string | null
	rate_expiry_date_local: string | null
	rate_uploaded_at: string | null
	rate_uploaded_by: IUser | null
	direct_hotel_booking_exists?: boolean
	same_black_out_date_intervals_for_all_room_types: boolean
	suppliers?: ISupplier[]
	same_sold_out_date_intervals_for_all_room_types: boolean
	channel_manager?: THotelChannelManager | null
	ota_rate_plans?: Array<THotelOtaRatePlan>
	// only returned by /hotels/:hotelId
	amenities?: Array<TAmenity>
	// only returned by /hotels when asked for in the `include` param
	total_rooms_count?: number
	total_rate_sheets_count?: number
	maps_url?: string
	brand_code?: string
}

export type THotelBrandCodeAvailability = {
	brand_code: string
	is_available: boolean
}

// Hotel CRM build (ota-core): every hotel is remote/CM-connected by
// definition, so this is just "a hotel is selected" — kept as a function
// rather than inlined so the local-hotel code paths this still gates stay
// intact for when this diverges from the Travel CRM branch.
export function isRemoteHotel(hotel: IHotel | undefined | null): boolean {
	return Boolean(hotel)
}

type THotelMeta = {
	meal_categories: {
		id: number
		name: string
		meals: string[]
	}[]
}

// The channel manager's pax types a hotel's child ages can be mapped to. The key
// has the channel manager so changing it fetches the new one's pax types.
export function useHotelRemotePaxTypes(
	channelManager: THotelChannelManager | null | undefined
) {
	const xhr = useXHR()
	const { data, error } = useSWR(
		channelManager
			? ([`/api/hotels/remote-pax-types`, channelManager] as const)
			: null,
		([, key]) => hotelsXHR(xhr).getRemotePaxTypes(key)
	)
	return {
		remotePaxTypes: data,
		isFetching: Boolean(channelManager) && !data && !error,
		error,
	}
}

// A nullish or non-numeric id skips the request, so callers that only know
// their hotel once some other state settles can still call this unconditionally.
export function useHotel(hotelId: string | number | null | undefined) {
	const xhr = useXHR()
	const hasId =
		hotelId !== null && hotelId !== undefined && !isNaN(Number(hotelId))
	const {
		data,
		mutate: revalidate,
		isValidating,
		error,
	} = useSWR(hasId ? `/api/hotels/${hotelId}` : null, () =>
		xhr
			.get<{ data: IHotel; meta: THotelMeta }>(`/hotels/${hotelId}`, {
				params: {
					include: "with_suppliers",
				},
			})
			.then((resp) => resp.data)
	)
	const isFetching = hasId && !data && !error
	const isRefreshing = isValidating
	return {
		hotel: data?.data,
		meta: data?.meta,
		fetchHotel: revalidate,
		isFetching,
		isRefreshing,
		error,
	}
}

interface ISimpleListResponseWithNotes<Item> extends ISimpleListResponse<Item> {
	meta: ISimpleListResponse<Item>["meta"] & {
		general_hotel_notes?: THotelNote[]
	}
}
export function hotelsXHR(xhr: XHRInstance) {
	return {
		async getHotels(
			params?: Record<string, unknown>
		): Promise<ISimpleListResponseWithNotes<IHotel>> {
			return xhr
				.get("/hotels", { params: { ...params, pagination: "simple" } })
				.then((resp) => resp.data)
		},
		async getHotelsWithTotal(params?: unknown): Promise<IListResponse<IHotel>> {
			return xhr.get("/hotels", { params }).then((resp) => resp.data)
		},
		async createHotel(data: FormData): Promise<IHotel> {
			return xhr
				.post<{ data: IHotel }>("/hotels", data)
				.then((resp) => resp.data.data)
		},
		async updateHotel(hotelId: number, data: FormData): Promise<IHotel> {
			return xhr
				.patch<{ data: IHotel }>(`/hotels/${hotelId}`, data)
				.then((resp) => resp.data.data)
		},
		// `hotelId` is the hotel being edited, so its own brand code counts as free
		async checkBrandCodeAvailability(
			brandCode: string,
			hotelId?: number
		): Promise<THotelBrandCodeAvailability> {
			return xhr
				.get<{
					data: THotelBrandCodeAvailability
				}>(`/hotels/check-brand-code-availability/${brandCode}`, {
					params: hotelId ? { hotel_id: hotelId } : undefined,
				})
				.then((resp) => resp.data.data)
		},
		async getHotelStarCategories(params?: unknown) {
			return xhr
				.get<{ data: Array<string> }>("/hotel-star-categories", { params })
				.then((resp) => resp.data.data.map((d) => ({ id: d, name: d })))
		},
		async getHotelLocations(params?: unknown) {
			return xhr
				.get<
					ISimpleListResponse<THotelLocation>
				>("/hotel-locations", { params })
				.then((resp) => resp.data.data)
		},
		async getHotelContacts(params?: unknown) {
			return xhr
				.get<ISimpleListResponse<IContact>>("/contacts", { params })
				.then((resp) => resp.data.data)
		},
		async createContact(
			id: string | number,
			contactData: object
		): Promise<IContact> {
			return xhr
				.post(`/hotel-contacts`, {
					hotel_id: id,
					...contactData,
				})
				.then((resp) => resp.data.data)
		},
		async saveOtaChildAges(
			hotelId: string | number,
			data: TOtaChildAgesPayload
		): Promise<Array<THotelOtaChildAge>> {
			return xhr
				.post<{
					data: Array<THotelOtaChildAge>
				}>(`/hotels/${hotelId}/ota-child-ages`, data)
				.then((resp) => resp.data.data)
		},
		async getRemotePaxTypes(
			channelManager: THotelChannelManager
		): Promise<THotelRemotePaxTypes> {
			return xhr
				.get<{
					data: THotelRemotePaxTypes
				}>("/hotels/remote-pax-types", {
					params: { channel_manager: channelManager },
				})
				.then((resp) => resp.data.data)
		},
		async updateChannelManager(
			hotelId: number,
			data: { channel_manager: THotelChannelManager | null }
		): Promise<{ message: string }> {
			return xhr
				.patch<{ message: string }>(`/hotels/${hotelId}/channel-manager`, data)
				.then((resp) => resp.data)
		},
		async updateCancellationPolicy(
			hotelId: number,
			data: { cancellation_policy_head_id: number }
		): Promise<unknown> {
			return xhr
				.patch(`/hotels/${hotelId}/cancellation-policy`, data)
				.then((resp) => resp.data)
		},
		async getCalendarAri(
			hotelId: number,
			params: {
				start_date: string
				end_date: string
				ota_meal_plan_id: number
				trip_source_id?: number
			}
		): Promise<THotelCalendarAri> {
			return xhr
				.get<{
					data: THotelCalendarAri
				}>(`/hotels/${hotelId}/calendar-ari`, { params })
				.then((resp) => resp.data.data)
		},
		async updatePaymentPolicy(
			hotelId: number,
			data: { payment_policy_head_id: number }
		): Promise<unknown> {
			return xhr
				.patch(`/hotels/${hotelId}/payment-policy`, data)
				.then((resp) => resp.data)
		},
	}
}

function isSoldOutInterval(interval: { stop_sales?: number | boolean }) {
	return interval.stop_sales === 1 || interval.stop_sales === true
}

function isBlackoutInterval(interval: { stop_sales?: number | boolean }) {
	return !isSoldOutInterval(interval)
}

function buildSimpleIntervals(
	roomTypes: IHotel["room_types"],
	matches: (interval: { stop_sales?: number | boolean }) => boolean
): TDateInterval[] {
	const intervalsMap = new Map<string, TDateInterval>()

	roomTypes?.forEach((room) => {
		room.black_out_date_intervals?.forEach((interval) => {
			if (!matches(interval)) return

			const key = `${interval.start_date}_${interval.end_date}`
			if (!intervalsMap.has(key)) {
				intervalsMap.set(key, {
					start_date: parseDate(interval.start_date),
					end_date: parseDate(interval.end_date),
				})
			}
		})
	})

	return Array.from(intervalsMap.values())
}

function buildGroupedRoomIntervals(
	roomTypes: IHotel["room_types"],
	matches: (interval: { stop_sales?: number | boolean }) => boolean,
	blackoutFlag: 0 | 1
): TGroupedRoomInterval[] {
	const intervalMap = new Map<string, TGroupedRoomInterval>()

	roomTypes?.forEach((room) => {
		room.black_out_date_intervals?.forEach((interval) => {
			if (!matches(interval)) return

			const intervalKey = `${interval.start_date}_${interval.end_date}`
			if (!intervalMap.has(intervalKey)) {
				intervalMap.set(intervalKey, {
					black_out_date_intervals: [
						{
							start_date: parseDate(interval.start_date),
							end_date: parseDate(interval.end_date),
						},
					],
					room_types: [],
					blackout: isTruthy(blackoutFlag),
				})
			}

			const group = intervalMap.get(intervalKey)
			if (!group) return

			if (!group.room_types.some((r) => r.value === room.id)) {
				group.room_types.push({
					name: room.name,
					value: room.id,
				})
			}
		})
	})

	const finalMap = new Map<string, TGroupedRoomInterval>()
	Array.from(intervalMap.values()).forEach((group) => {
		const sortedRoomIds = group.room_types
			.map((r) => r.value)
			.sort((a, b) => a - b)
			.join("_")

		const finalKey = `${sortedRoomIds}_${group.blackout}`
		const existing = finalMap.get(finalKey)
		if (existing) {
			existing.black_out_date_intervals.push(...group.black_out_date_intervals)
		} else {
			finalMap.set(finalKey, { ...group })
		}
	})

	return Array.from(finalMap.values())
}

export function formatClosedDates(
	roomTypes: IHotel["room_types"],
	same_black_out_date_intervals_for_all_room_types: boolean,
	same_sold_out_date_intervals_for_all_room_types: boolean
) {
	const result: {
		stop_sale_date_intervals?: TDateInterval[]
		blackout_date_intervals?: TDateInterval[]
		stop_sale_date_for_room_types?: TSoldoutRoomInterval[]
		blackout_date_for_room_types?: TBlackoutRoomInterval[]
	} = {}

	// ---------- Soldout ----------
	if (same_sold_out_date_intervals_for_all_room_types) {
		result.stop_sale_date_intervals = buildSimpleIntervals(
			roomTypes,
			isSoldOutInterval
		)
	} else {
		const soldoutGroups = buildGroupedRoomIntervals(
			roomTypes,
			isSoldOutInterval,
			0
		) as TSoldoutRoomInterval[]

		if (soldoutGroups?.length > 0) {
			result.stop_sale_date_for_room_types = soldoutGroups
		}
	}

	// ---------- Blackout ----------
	if (same_black_out_date_intervals_for_all_room_types) {
		result.blackout_date_intervals = buildSimpleIntervals(
			roomTypes,
			isBlackoutInterval
		)
	} else {
		const blackoutGroups = buildGroupedRoomIntervals(
			roomTypes,
			isBlackoutInterval,
			1
		) as TBlackoutRoomInterval[]

		if (blackoutGroups?.length > 0) {
			result.blackout_date_for_room_types = blackoutGroups
		}
	}

	return result
}

type GetGlobalNotesForHotelDestinationParams = {
	hotelDestination?: TTripDestination[]
	generalNotes?: THotelNote[]
}

export function getGlobalNotesForHotelDestination({
	hotelDestination,
	generalNotes,
}: GetGlobalNotesForHotelDestinationParams) {
	if (!generalNotes?.length) {
		return generalNotes
	}

	const destinationIds = new Set(
		hotelDestination?.map((destination) => destination.id) || []
	)
	return generalNotes.filter((note) => {
		//here if tripDestinations is empty it means that hotel is associated with all destinations
		if (!hotelDestination?.length || !note?.destinations?.length) {
			return true
		}

		return note.destinations.some((destination) =>
			destinationIds.has(destination.id)
		)
	})
}
